import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Chip,
  Skeleton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../../api/client";
import { useCompanies } from "../../../hooks/useCompanies";
import i18n, {
  EAGER_NAMESPACES,
  LAZY_NAMESPACES,
  getGlobalLocalesForUiSelect,
} from "../../../i18n/config";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
function normalizeCodePart(input = "") {
  return String(input || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 24);
}

function buildWorkspaceLanguageCode(baseLang, label) {
  const base = String(baseLang || "pt-BR").toLowerCase();
  const suffix = normalizeCodePart(label);
  return suffix ? `${base}-x-${suffix}` : `${base}-x-custom`;
}

const CATALOG_NAMESPACES = Array.from(
  new Set([...EAGER_NAMESPACES, ...LAZY_NAMESPACES, "settings_i18n"])
).filter(Boolean);

function flattenTranslationObject(input, prefix = "", out = []) {
  if (!input || typeof input !== "object") return out;
  for (const [key, value] of Object.entries(input)) {
    const nextPath = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      flattenTranslationObject(value, nextPath, out);
    } else if (["string", "number", "boolean"].includes(typeof value)) {
      out.push(nextPath);
    }
  }
  return out;
}

function buildLocalCatalogFromUiResources() {
  const resources = i18n?.options?.resources || {};
  const defaultLocale = resources["pt-BR"]
    ? "pt-BR"
    : Object.keys(resources).find(Boolean);
  if (!defaultLocale) return [];

  const localeResources = resources[defaultLocale] || {};
  const rows = [];
  for (const namespace of CATALOG_NAMESPACES) {
    const nsObj = localeResources[namespace];
    const keys = flattenTranslationObject(nsObj);
    for (const key of keys) {
      rows.push({ id: null, namespace, key, description: null });
    }
  }
  return rows;
}

function buildFlatLocaleValueMap(localeCode) {
  const resources = i18n?.options?.resources || {};
  const code = String(localeCode || "").trim();
  const localeResources = resources[code] || {};
  const pairs = [];
  for (const namespace of CATALOG_NAMESPACES) {
    const keys = flattenTranslationObject(localeResources[namespace]);
    for (const key of keys) {
      const value = key
        .split(".")
        .reduce((acc, part) => (acc && typeof acc === "object" ? acc[part] : undefined), localeResources[namespace]);
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        pairs.push([`${namespace}::${key}`, String(value)]);
      }
    }
  }
  return new Map(pairs);
}

function getNamespaceLabel(namespace, t) {
  const labels = {
    app: t("module_app"),
    auth: t("module_auth"),
    chat: t("module_chat"),
    chatList: t("module_chatList"),
    chatbot: t("module_chatbot"),
    common: t("module_common"),
    dashboard: t("module_dashboard"),
    dataGrid: t("module_dataGrid"),
    errors: t("module_errors"),
    onboarding: t("module_onboarding"),
    products: t("module_products"),
    profile: t("module_profile"),
    settings: t("module_settings"),
    settings_i18n: t("module_settings_i18n"),
    shell: t("module_shell"),
    themes: t("module_themes"),
  };
  return labels[namespace] || namespace;
}

function getFriendlyKeyLabel(namespace, key, t) {
  const map = {
    "app.loading": t("term_loading"),
    "app.nav.chat": t("term_requests_menu"),
    "app.nav.clients": t("term_clients_menu"),
    "app.nav.salesPipeline": t("term_sales_pipeline"),
    "app.nav.products": t("term_products_menu"),
    "app.nav.dashboard": t("term_dashboard_menu"),
    "common.status.active": t("term_status_active"),
    "common.status.inactive": t("term_status_inactive"),
    "errors.auth.invalid_credentials": t("term_invalid_credentials"),
  };
  return map[`${namespace}.${key}`] || key;
}

export default function WorkspaceI18nSettings() {
  const { t } = useTranslation("settings_i18n");
  const [projectId, setProjectId] = useState("");
  const [languages, setLanguages] = useState([]);
  const [selectedWl, setSelectedWl] = useState("");
  const [overrides, setOverrides] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [baseLang, setBaseLang] = useState("pt-BR");
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedNamespace, setSelectedNamespace] = useState("app");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showTechnical, setShowTechnical] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const { data: companiesData, isLoading: companiesLoading } = useCompanies(1, 500, "");
  const companies = companiesData?.companies || [];

  const selectedCompanyIds = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("selectedCompanies");
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr)
        ? arr.map((v) => Number(v)).filter((v) => Number.isFinite(v))
        : [];
    } catch {
      return [];
    }
  }, []);

  const projectOptions = useMemo(() => {
    const map = new Map(companies.map((c) => [Number(c.id), c]));
    const ids =
      selectedCompanyIds.length > 0
        ? selectedCompanyIds
        : companies.map((c) => Number(c.id));
    return ids.map((pid) => {
      const c = map.get(Number(pid));
      const labelBase = c?.nome || c?.fantasia || c?.razao_social || `${t("company")} #${pid}`;
      return { id: Number(pid), label: `${labelBase} (ID ${pid})` };
    });
  }, [companies, selectedCompanyIds, t]);

  useEffect(() => {
    if (!projectId && projectOptions.length > 0) {
      setProjectId(String(projectOptions[0].id));
    }
  }, [projectId, projectOptions]);

  const loadLanguages = useCallback(async () => {
    if (!projectId) return;
    setErr(null);
    try {
      const res = await fetchData(
        `/i18n/workspace-languages?id_projeto=${encodeURIComponent(projectId)}`,
        "GET"
      );
      const items = res?.data?.items ?? res?.items ?? [];
      const list = Array.isArray(items) ? items : [];
      const onlySelectedCompany = list.filter(
        (item) => Number(item?.id_projeto) === Number(projectId)
      );
      setLanguages(onlySelectedCompany);
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }, [projectId]);

  useEffect(() => {
    loadLanguages();
  }, [loadLanguages]);

  useEffect(() => {
    setSelectedWl("");
    setOverrides([]);
    setSelectedNamespace("app");
  }, [projectId]);

  const loadCatalog = async () => {
    if (!selectedWl) return;
    setErr(null);
    setCatalogLoading(true);
    try {
      const selectedLanguage = languages.find((l) => String(l.id) === String(selectedWl));
      const baseLocaleCode = selectedLanguage?.base_code || "pt-BR";
      const baseValueMap = buildFlatLocaleValueMap(baseLocaleCode);
      const ptBrValueMap = buildFlatLocaleValueMap("pt-BR");

      const requestedNamespaces =
        selectedNamespace === "all"
          ? CATALOG_NAMESPACES
          : CATALOG_NAMESPACES.filter((ns) => ns === selectedNamespace);
      const qs = encodeURIComponent(requestedNamespaces.join(","));
      const [res, ov] = await Promise.all([
        fetchData(`/i18n/catalog?namespaces=${qs}`, "GET"),
        fetchData(
          `/i18n/workspace-overrides?workspace_language_id=${encodeURIComponent(selectedWl)}`,
          "GET"
        ),
      ]);

      const items = res?.data?.items ?? res?.items ?? [];
      const apiCatalog = Array.isArray(items) ? items : [];
      const localCatalog = buildLocalCatalogFromUiResources().filter((row) =>
        requestedNamespaces.includes(row.namespace)
      );
      const byComposite = new Map();
      for (const row of apiCatalog) {
        byComposite.set(`${row.namespace}::${row.key}`, row);
      }
      for (const row of localCatalog) {
        const composite = `${row.namespace}::${row.key}`;
        if (!byComposite.has(composite)) byComposite.set(composite, row);
      }
      const mergedCatalog = Array.from(byComposite.values()).sort((a, b) =>
        `${a.namespace}.${a.key}`.localeCompare(`${b.namespace}.${b.key}`)
      );
      const oitems = ov?.data?.items ?? ov?.items ?? [];
      const byId = new Map();
      const byCompositeOverride = new Map();
      for (const row of oitems || []) {
        byId.set(row.key_id, row.value);
        byCompositeOverride.set(`${row.namespace}::${row.key}`, row.value);
      }
      setOverrides(
        mergedCatalog.map((k) => {
          const composite = `${k.namespace}::${k.key}`;
          const overrideValue =
            (k.id ? byId.get(k.id) : undefined) ??
            byCompositeOverride.get(composite);
          const baseValue = baseValueMap.get(composite);
          const ptValue = ptBrValueMap.get(composite);
          const isLegacyPtClone =
            overrideValue != null &&
            overrideValue !== "" &&
            baseLocaleCode !== "pt-BR" &&
            baseValue != null &&
            baseValue !== "" &&
            ptValue != null &&
            ptValue !== "" &&
            String(overrideValue) === String(ptValue);
          const displayValue = isLegacyPtClone ? baseValue : overrideValue ?? baseValue ?? "";

          return {
            ...k,
            rowKey: k.id ? `id:${k.id}` : `nk:${k.namespace}:${k.key}`,
            value: displayValue,
            initialValue: displayValue,
          };
        })
      );
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setCatalogLoading(false);
    }
  };

  useEffect(() => {
    if (selectedWl) loadCatalog();
    else {
      setOverrides([]);
      setSelectedNamespace("app");
    }
  }, [selectedWl, selectedNamespace]);

  const generatedCode = buildWorkspaceLanguageCode(baseLang, newLabel);

  const handleCreate = async () => {
    if (!projectId || !newLabel.trim()) return;
    setLoading(true);
    setMsg(null);
    setErr(null);
    try {
      await fetchData("/i18n/workspace-languages", "POST", {
        id_projeto: Number(projectId),
        code: generatedCode,
        label: newLabel.trim(),
        base_language_code: baseLang,
      });
      setMsg(t("created"));
      setNewLabel("");
      await loadLanguages();
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const saveOverride = async (row) => {
    if (!selectedWl) return;
    setErr(null);
    try {
      const item = row.id
        ? { key_id: row.id, value: row.value ?? "" }
        : { namespace: row.namespace, key: row.key, value: row.value ?? "" };
      await fetchData("/i18n/workspace-overrides", "PUT", {
        workspace_language_id: Number(selectedWl),
        items: [item],
      });
      setMsg(t("saveOverride"));
    } catch (e) {
      setErr(e?.message || String(e));
    }
  };

  const openDeleteLanguageDialog = () => {
    if (!selectedWl) return;
    setDeleteDialogOpen(true);
  };

  const handleDeleteLanguage = async () => {
    if (!selectedWl) return;
    setDeleting(true);
    setMsg(null);
    setErr(null);
    try {
      await fetchData(`/i18n/workspace-languages/${encodeURIComponent(selectedWl)}`, "DELETE");
      setSelectedWl("");
      setOverrides([]);
      await loadLanguages();
      setMsg(t("deleted"));
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleOverrideChange = (rowKey, value) => {
    setOverrides((prev) =>
      prev.map((row) => (row.rowKey === rowKey ? { ...row, value } : row))
    );
  };

  const namespaceOptions = useMemo(() => {
    return Array.from(new Set(CATALOG_NAMESPACES)).sort();
  }, []);

  const visibleRows = useMemo(() => {
    if (selectedNamespace === "all") return overrides;
    return overrides.filter((row) => row.namespace === selectedNamespace);
  }, [overrides, selectedNamespace]);

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return visibleRows;
    return visibleRows.filter((row) => {
      const friendly = getFriendlyKeyLabel(row.namespace, row.key, t).toLowerCase();
      return (
        row.key.toLowerCase().includes(q) ||
        String(row.value || "").toLowerCase().includes(q) ||
        getNamespaceLabel(row.namespace, t).toLowerCase().includes(q) ||
        friendly.includes(q)
      );
    });
  }, [visibleRows, searchTerm, t]);

  const dirtyRows = useMemo(() => {
    return overrides.filter((row) => String(row.value ?? "") !== String(row.initialValue ?? ""));
  }, [overrides]);

  const mainTerms = useMemo(() => {
    const picks = [
      ["app", "nav.chat"],
      ["app", "nav.clients"],
      ["app", "nav.salesPipeline"],
      ["common", "status.active"],
      ["common", "status.inactive"],
      ["errors", "auth.invalid_credentials"],
    ];
    const map = new Map(overrides.map((row) => [`${row.namespace}::${row.key}`, row]));
    return picks
      .map(([namespace, key]) => map.get(`${namespace}::${key}`))
      .filter(Boolean);
  }, [overrides]);

  const selectedLanguageLabel = useMemo(() => {
    const selected = languages.find((l) => String(l.id) === String(selectedWl));
    return selected?.label || selected?.code || selectedWl;
  }, [languages, selectedWl]);

  const saveAllChanges = async () => {
    if (!selectedWl || !dirtyRows.length) return;
    setSavingAll(true);
    setErr(null);
    try {
      const items = dirtyRows.map((row) =>
        row.id
          ? { key_id: row.id, value: row.value ?? "" }
          : { namespace: row.namespace, key: row.key, value: row.value ?? "" }
      );
      await fetchData("/i18n/workspace-overrides", "PUT", {
        workspace_language_id: Number(selectedWl),
        items,
      });
      setOverrides((prev) => prev.map((row) => ({ ...row, initialValue: row.value ?? "" })));
      setMsg(t("savedAll", { count: items.length }));
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setSavingAll(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, maxWidth: 1240, mx: "auto" }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
        {t("title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("subtitle")}
      </Typography>

      <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Stack spacing={1.2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {t("guideTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("guideIntro")}
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
            <Chip size="small" label={t("guideStep1")} />
            <Chip size="small" label={t("guideStep2")} />
            <Chip size="small" label={t("guideStep3")} />
            <Chip size="small" label={t("guideStep4")} />
          </Stack>
        </Stack>
      </Paper>

      {msg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg}
        </Alert>
      )}
      {err && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
          {err}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t("company")}
            </Typography>
            <Chip
              size="small"
              color="default"
              variant="outlined"
              label={`${languages.length} ${t("selectLanguage").toLowerCase()}`}
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 320 }}>
              <InputLabel>{t("company")}</InputLabel>
              <Select
                value={projectId}
                label={t("company")}
                onChange={(e) => setProjectId(String(e.target.value || ""))}
                disabled={companiesLoading || !projectOptions.length}
              >
                {projectOptions.map((option) => (
                  <MenuItem key={option.id} value={String(option.id)}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{t("companyHelp")}</FormHelperText>
            </FormControl>
          </Stack>
        </Stack>
        {!projectId ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t("workspaceRequired")}
          </Alert>
        ) : null}
      </Paper>

      <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          {t("create")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("createHelp")}
        </Typography>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="stretch"
        >
          <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
            <InputLabel>{t("baseLanguage")}</InputLabel>
            <Select value={baseLang} label={t("baseLanguage")} onChange={(e) => setBaseLang(e.target.value)}>
              {getGlobalLocalesForUiSelect().map(({ code, nativeName }) => (
                <MenuItem key={code} value={code}>
                  {code} — {nativeName}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>&nbsp;</FormHelperText>
          </FormControl>
          <TextField
            label={t("customLabel")}
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            size="small"
            sx={{ minWidth: 280, flex: 1 }}
            helperText={t("customLabelHelp")}
          />
          <TextField
            label={t("generatedCode")}
            value={generatedCode}
            size="small"
            sx={{ minWidth: 260, flex: 1 }}
            InputProps={{ readOnly: true }}
            helperText=" "
          />
          <Button
            variant="contained"
            disabled={loading || !projectId || !newLabel.trim()}
            onClick={handleCreate}
            sx={{
              minHeight: 40,
              minWidth: 220,
              alignSelf: { xs: "stretch", md: "flex-start" },
              mt: { xs: 0, md: "3px" },
            }}
          >
            {t("create")}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
        <Box>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
            <FormControl fullWidth size="small" sx={{ maxWidth: 520 }}>
              <InputLabel>{t("selectLanguage")}</InputLabel>
              <Select
                value={selectedWl}
                label={t("selectLanguage")}
                onChange={(e) => setSelectedWl(e.target.value)}
              >
                {languages.map((l) => (
                  <MenuItem key={l.id} value={String(l.id)}>
                    {l.label} ({l.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              color="error"
              disabled={!selectedWl || deleting}
              onClick={openDeleteLanguageDialog}
              sx={{ minWidth: 170, flexShrink: 0 }}
            >
              {t("deleteLanguage")}
            </Button>
          </Stack>
          <FormHelperText sx={{ mt: 0.5, maxWidth: 520, mx: 0 }}>
            {t("selectLanguageHelp")}
          </FormHelperText>
        </Box>
        {!languages.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t("noLanguages")}
          </Typography>
        ) : null}
      </Paper>

      {selectedWl && mainTerms.length ? (
        <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t("mainTermsTitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("mainTermsHelp")}
            </Typography>
          </Stack>
          <Stack spacing={1.2}>
            {mainTerms.map((row) => (
              <Stack key={`main-${row.rowKey}`} direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems="center">
                <Typography sx={{ minWidth: 280, fontWeight: 600 }}>
                  {getFriendlyKeyLabel(row.namespace, row.key, t)}
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={row.value ?? ""}
                  onChange={(e) => handleOverrideChange(row.rowKey, e.target.value)}
                />
              </Stack>
            ))}
          </Stack>
        </Paper>
      ) : null}

      {selectedWl ? (
        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t("overrides")}
            </Typography>
            <TextField
              size="small"
              placeholder={t("searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 240, maxWidth: 320 }}>
              <InputLabel>{t("namespace")}</InputLabel>
              <Select
                value={selectedNamespace}
                label={t("namespace")}
                onChange={(e) => setSelectedNamespace(String(e.target.value || "all"))}
              >
                <MenuItem value="all">{t("allModules")}</MenuItem>
                {namespaceOptions.map((ns) => (
                  <MenuItem key={ns} value={ns}>
                    {getNamespaceLabel(ns, t)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={showTechnical} onChange={(e) => setShowTechnical(e.target.checked)} />}
              label={t("showTechnical")}
              sx={{ ml: { md: 0 } }}
            />
            <Button
              variant="contained"
              onClick={saveAllChanges}
              disabled={!dirtyRows.length || savingAll}
              sx={{ minWidth: 190 }}
            >
              {savingAll ? t("saving") : t("saveAll", { count: dirtyRows.length })}
            </Button>
          </Stack>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {t("overridesHelp")}
          </Alert>
          <TableContainer sx={{ maxHeight: 520, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t("namespace")}</TableCell>
                  <TableCell>{showTechnical ? t("key") : t("termLabel")}</TableCell>
                  <TableCell>{t("value")}</TableCell>
                  <TableCell width={120} />
                </TableRow>
              </TableHead>
              <TableBody>
                {catalogLoading
                  ? Array.from({ length: 8 }).map((_, idx) => (
                      <TableRow key={`skeleton-${idx}`}>
                        <TableCell><Skeleton variant="text" width={110} /></TableCell>
                        <TableCell><Skeleton variant="text" width={180} /></TableCell>
                        <TableCell><Skeleton variant="rounded" height={38} /></TableCell>
                        <TableCell><Skeleton variant="rounded" width={84} height={30} /></TableCell>
                      </TableRow>
                    ))
                  : filteredRows.map((row) => (
                      <TableRow key={row.rowKey}>
                        <TableCell>{getNamespaceLabel(row.namespace, t)}</TableCell>
                        <TableCell>{showTechnical ? row.key : getFriendlyKeyLabel(row.namespace, row.key, t)}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={row.value ?? ""}
                            onChange={(e) => handleOverrideChange(row.rowKey, e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={String(row.value ?? "") === String(row.initialValue ?? "")}
                            onClick={() => saveOverride(row)}
                          >
                            {t("saveOverride")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}

      <ConfirmDialog
        open={deleteDialogOpen}
        title={t("deleteDialogTitle")}
        content={deleting ? t("processingDelete") : t("deleteConfirm", { label: selectedLanguageLabel })}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteLanguage}
        confirmText={t("deleteLanguage")}
        cancelText={t("cancel")}
        confirmColor="error"
        loading={deleting}
      />
    </Box>
  );
}
